import { useState, useEffect, useRef } from 'react';

import {
  getEasing,
} from 'common/parse-tokens';

let animationFrame;
let stopTimer;
let startTimer;

/*
 * The `useKeyFrameAnimationTimer` hook calls useState every animation frame,
 * giving us elapsed time and causing a re-render as frequently as possible for a smooth animation.
 */
const useKeyFrameAnimationTimer = (key, duration = 1000) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);
  useEffect(() => {
    clearTimeout(stopTimer);
    cancelAnimationFrame(animationFrame);
    // Call onFrame() on next animation frame
    const loop = () => {
      animationFrame = requestAnimationFrame(() => {
        isMounted.current && setElapsedTime(Date.now() - startTimer);
        loop();
      });
    };
    // onStart is executed on each animation frame
    const onStart = () => {
      // `stopTimer` stops getting animation frames when duration time elapses
      stopTimer = setTimeout(() => {
        cancelAnimationFrame(animationFrame);
        setElapsedTime(Date.now() - startTimer);
      }, duration);
      // Start the loop
      startTimer = Date.now();
      loop();
    };
    onStart();
    return () => {
      clearTimeout(stopTimer);
      cancelAnimationFrame(animationFrame);
    };
  }, [duration, key]);
  return elapsedTime;
};

/**
 * `useAnimation` generates values between 0 and 1 in given duration and eased using passed easing.
 * To restart pass a different key.
 */
const useAnimation = (easing, duration, key) => {
  let result = 0;
  let value = 0;
  const elapsedTime = useKeyFrameAnimationTimer(key, duration);
  value = Math.min(1, elapsedTime / duration);
  result = getEasing(easing)(value);
  return result;
};

export default useAnimation;